<template>
  <FlotoAnchorLink :aria-label="$tc('merge')" @click="triggerAction">
    <MIcon name="merge" class="action-menu-icon" />
    <span class="ml-1 action-menu-text">
      {{ $tc('merge') }}
    </span>
    <FlotoDrawer :open="showDrawer" width="85%" @hide="hide">
      <template v-slot:title>
        {{ $t('merge') }} {{ $tc('request', 2) }}
      </template>
      <MRow class="flex-1">
        <MCol :size="4" class="flex">
          <FlotoFixedView>
            <MRow :gutter="0" class="h-100">
              <MCol :size="12" class="hierarchy-explorer flex flex-col h-100">
                <FlotoScrollView>
                  <FlotoFormItem
                    id="primary-note-input"
                    v-model="primaryNote"
                    class="font-italic"
                    :label="`${$t('primary')} ${$tc('request')} ${$tc(
                      'comment'
                    )}`"
                  />
                  <ul class="sortable-list">
                    <li>
                      <RequestItem
                        :request="primary"
                        :module-name="moduleName"
                        is-primary
                      />
                      <TransitionGroup
                        name="slideRight"
                        tag="ul"
                        style="padding-left: 30px"
                        class="sortable-list-child"
                        mode="out-in"
                      >
                        <li key="secondary-note" class="secondary-note-comment">
                          <FlotoFormItem
                            id="secondary-note-input"
                            v-model="secondaryNote"
                            :disabled="!secondary.length"
                            class="font-italic"
                            :label="`${$t('secondary')} ${$tc('request')} ${$tc(
                              'comment'
                            )}`"
                          />
                        </li>
                        <li
                          v-for="request in secondary"
                          :key="request.id"
                          class="my-4"
                        >
                          <RequestItem
                            :module-name="moduleName"
                            :request="request"
                          >
                            <template v-slot:actions>
                              <MTooltip v-if="!defautlPrimary">
                                <template v-slot:trigger>
                                  <MButton
                                    id="make-primary-btn"
                                    size="small"
                                    outline
                                    @click="makePrimary(request)"
                                  >
                                    P
                                  </MButton>
                                </template>
                                {{ $t('make') }} {{ $t('primary') }}
                              </MTooltip>
                              <MButton
                                id="cancel-btn"
                                size="small"
                                outline
                                class="ml-2"
                                @click="removeSecondary(request)"
                              >
                                <MIcon name="times" />
                              </MButton>
                            </template>
                          </RequestItem>
                        </li>
                      </TransitionGroup>
                    </li>
                  </ul>
                </FlotoScrollView>
              </MCol>
            </MRow>
          </FlotoFixedView>
        </MCol>
        <MCol :size="8" class="left-shadow-only overflow-hidden">
          <MRow class="flex flex-1 h-100">
            <MCol class="flex flex-1 flex-col overflow-hidden">
              <TicketSelectionList
                ref="allRequestsList"
                :module-name="$constants.REQUEST"
                searchable
                selectable
                :exclude-items-by-id="excludeItemsById"
                :fetch-fn="getRemainingRequests"
                @selection-change="selectedTicketsToMarkAsSecondary = $event"
              >
                <template
                  v-slot="{ selectedItemIds: requestsToAddAsSecondary, reset }"
                >
                  <div
                    v-if="requestsToAddAsSecondary.length"
                    class="text-left mt-2"
                  >
                    <MButton
                      id="add-to-secondary-btn"
                      outline
                      size="small"
                      @click="addItemsToSecondary(reset)"
                    >
                      {{ $t('add') }} {{ $t('to') }} {{ $t('secondary') }}
                    </MButton>
                  </div>
                </template>
              </TicketSelectionList>
            </MCol>
          </MRow>
        </MCol>
      </MRow>
      <template v-slot:actions="">
        <MButton
          id="merge-btn"
          class="mr-2"
          :disabled="secondary.length <= 0"
          :loading="processing"
          @click="handleMergeRequests"
        >
          {{ $t('merge') }}
        </MButton>
        <MButton id="cancel-btn" variant="default" @click="hide">
          {{ $t('cancel') }}
        </MButton>
      </template>
    </FlotoDrawer>
  </FlotoAnchorLink>
</template>

<script>
import {
  transformSearchCriteriasForServer,
  transformSearchCriteriaForServer,
} from '@data/search'
import { searchTicketsApi, mergeRequestApi } from '../../../ticket-api'
import RequestItem from './request-item'

export default {
  name: 'Merge',
  components: {
    TicketSelectionList: () =>
      import('@components/item-selection-list/ticket-list.vue'),
    RequestItem,
  },
  props: {
    selectedItemIds: { type: Array, required: true },
    moduleName: { type: String, required: true },
    defautlPrimary: { type: Object, default: undefined },
  },
  data() {
    return {
      showDrawer: false,
      processing: false,
      primary: {},
      primaryNote: '',
      secondary: [],
      secondaryNote: '',
      selectedTicketsToMarkAsSecondary: [],
      selectedItems: [],
    }
  },
  computed: {
    excludeItemsById() {
      return [...this.secondary.map((i) => i.id), this.primary.id]
    },
  },
  methods: {
    getSelectedItems() {
      const criterias = transformSearchCriteriasForServer(this.moduleName, [])
      if (this.selectedItemIds.length) {
        const q = transformSearchCriteriaForServer(this.moduleName, {
          operator: { value: 'in' },
          paramName: 'id',
          value: this.selectedItemIds,
          valueType: 'long',
        })
        criterias.quals.push(q)
      }
      return searchTicketsApi(
        this.$constants.REQUEST,
        criterias,
        this.selectedItemIds.length,
        0
      ).then((data) => {
        this.selectedItems = data.items
        this.resetToDefault()
        this.showDrawer = true
      })
    },
    setPrimaryNote() {
      this.primaryNote = `Request ${this.secondary
        .map(({ name }) => name)
        .join(', ')} ${
        this.secondary.length > 1 ? 'are' : 'is'
      } merged with this request`
    },
    setSecondaryNote() {
      this.secondaryNote = `This request is merged with ${this.primary.name} request`
    },
    triggerAction() {
      this.$emit('trigger')
      setTimeout(() => {
        this.getSelectedItems()
      }, 400)
    },
    getRemainingRequests(_, search, limit, offset) {
      return searchTicketsApi(this.$constants.REQUEST, search, limit, offset)
    },
    addItemsToSecondary(refreshList) {
      this.secondary = Object.freeze([
        ...this.secondary,
        ...this.selectedTicketsToMarkAsSecondary,
      ])
      this.setPrimaryNote()
      this.$nextTick(() => {
        this.selectedTicketsToMarkAsSecondary = []
        refreshList()
      })
    },
    resetToDefault() {
      this.processing = false
      this.primary = this.defautlPrimary
        ? this.defautlPrimary
        : this.selectedItems[0]
      this.primaryNote = ''
      this.secondary = this.defautlPrimary
        ? this.selectedItems.slice(0)
        : this.selectedItems.slice(1)
      this.secondaryNote = ''
      this.selectedTicketsToMarkAsSecondary = []
      this.setPrimaryNote()
      this.setSecondaryNote()
    },
    hide() {
      this.resetToDefault()
      this.showDrawer = false
    },
    makePrimary(item) {
      this.secondary = Object.freeze([
        this.primary,
        ...this.secondary.filter((i) => i.id !== item.id),
      ])
      this.primary = Object.freeze(item)
      this.setPrimaryNote()
      this.setSecondaryNote()
    },
    removeSecondary(item) {
      this.secondary = Object.freeze(
        this.secondary.filter((i) => i.id !== item.id)
      )
      this.$nextTick(() => {
        this.$refs.allRequestsList.refresh()
      })
      this.setPrimaryNote()
    },
    handleMergeRequests() {
      this.processing = true
      mergeRequestApi(
        this.primary.id,
        this.secondary.map(({ id }) => id),
        this.primaryNote,
        this.secondaryNote
      )
        .then((data) => {
          this.showDrawer = false
          this.$emit('done')
        })
        .finally(() => (this.processing = false))
    },
  },
}
</script>

<style lang="less" scoped>
.secondary-note-comment {
  &::before {
    border-top-color: transparent !important;
  }
}
</style>
